import type { IconName } from "../../icons";

interface ItemBase {
  label: string;
  icon?: IconName;
  /** Small color square before the label (labels, boards). */
  swatch?: string;
  /** Right-aligned hint: a keyboard shortcut, a date… */
  hint?: string;
  disabled?: boolean;
  /** Why the item is disabled; shown in a tooltip and read by screen readers. Implies `disabled`. */
  disabledReason?: string;
  tone?: "default" | "danger";
}

export interface ContextMenuAction extends ItemBase {
  kind?: "action";
  onSelect: () => void;
}

/** Toggle; the menu stays open so several can be changed in a row. */
export interface ContextMenuCheckbox extends ItemBase {
  kind: "checkbox";
  checked: boolean;
  onSelect: () => void;
}

/** One choice of a set (priority, assignee); closes the menu. */
export interface ContextMenuRadio extends ItemBase {
  kind: "radio";
  checked: boolean;
  onSelect: () => void;
}

export interface ContextMenuSubmenu extends ItemBase {
  kind: "submenu";
  items: ContextMenuItem[];
}

export interface ContextMenuHeading {
  kind: "heading";
  label: string;
}

export type ContextMenuItem = ContextMenuAction | ContextMenuCheckbox | ContextMenuRadio | ContextMenuSubmenu | ContextMenuHeading | "separator";

/** An item the user can focus and activate: everything but separators and headings. */
export type InteractiveItem = Exclude<ContextMenuItem, "separator" | ContextMenuHeading>;

export function isInteractive(item: ContextMenuItem): item is InteractiveItem {
  return item !== "separator" && item.kind !== "heading";
}

export function isItemDisabled(item: InteractiveItem): boolean {
  return Boolean(item.disabled || item.disabledReason);
}

export function isSubmenu(item: ContextMenuItem | undefined): item is ContextMenuSubmenu {
  return item !== undefined && isInteractive(item) && item.kind === "submenu";
}

/** True when `item` is a submenu the user can open. */
export function isOpenableSubmenu(item: ContextMenuItem | undefined): boolean {
  return isSubmenu(item) && !isItemDisabled(item);
}

/** Positions (in `items`) of the interactive items, in order. */
export function interactiveIndexes(items: ContextMenuItem[]): number[] {
  return items.map((item, index) => (isInteractive(item) ? index : -1)).filter((index) => index >= 0);
}

/** The position that receives focus when a panel opens: the first enabled item, else the first one. */
export function initialFocusIndex(items: ContextMenuItem[]): number | undefined {
  const interactive = interactiveIndexes(items);
  return interactive.find((index) => !isItemDisabled(items[index] as InteractiveItem)) ?? interactive[0];
}
